import {
  getFirestore,
  doc,
  collection,
  query,
  orderBy,
  limit,
  where,
  QueryConstraint,
  Timestamp,
  writeBatch,
  addDoc,
} from "firebase/firestore";
import { Collection } from "firestorter";

import Document from "../general/document";
import Customer from "./customer";
import Employee from "./employee";
import HourType from "./hourtype";
import Location from "./location";
import Product from "./product";
import Report from "./report";
import Sync from "./sync";
import Vehicle from "./vehicle";
import WorkOrder, { WorkOrderData } from "./workorder";

export interface AdministrationData {
  description: string;
}

export interface Query {
  filter?: FilterData;
  order?: OrderData;
  page?: number;
}

export type OrderField =
  | "startAt"
  | "employee"
  | "product"
  | "customer"
  | "location"
  | "downloadedAt";
export type OrderDirection = "asc" | "desc";
export interface OrderData {
  field: OrderField;
  direction: OrderDirection;
}

export type FilterField =
  | "startAt"
  | "employee"
  | "product"
  | "customer"
  | "location";
export interface FilterData {
  [key: string]: any;
}

interface NewWorkOrderData {
  startAt: Date;
  endAt: Date;
}

export const PAGE_SIZE = 100;

class Administration extends Document<AdministrationData> {
  firestore = getFirestore();
  readonly sync = new Collection(
    collection(this.firestore, `multivers/${this.id}/sync`),
    {
      createDocument: (source, options) =>
        new Sync(source, options, this.store),
      query: (ref) => query(ref, orderBy("startAt", "desc")),
    }
  );
  readonly orders = new Collection(
    collection(this.firestore, `multivers/${this.id}/workorder`),
    {
      createDocument: (source, options) =>
        new WorkOrder(source, options, this.store),
      query: (ref) => query(ref, orderBy("startAt", "desc"), limit(PAGE_SIZE)),
    }
  );

  readonly employees = new Collection(
    collection(this.firestore, `multivers/${this.id}/employee`),
    {
      createDocument: (source, options) =>
        new Employee(source, options, this.store),
      query: (ref) => query(ref, orderBy("name")),
    }
  );

  readonly customers = new Collection(
    collection(this.firestore, `multivers/${this.id}/customer`),
    {
      createDocument: (source, options) =>
        new Customer(source, options, this.store),
      query: (ref) => query(ref, orderBy("name")),
    }
  );

  readonly vehicles = new Collection(
    collection(this.firestore, `multivers/${this.id}/project`),
    {
      createDocument: (source, options) =>
        new Vehicle(source, options, this.store),
      query: (ref) => query(ref, orderBy("projectId")),
    }
  );

  readonly products = new Collection(
    collection(this.firestore, `multivers/${this.id}/product`),
    {
      createDocument: (source, options) =>
        new Product(source, options, this.store),
      query: (ref) => query(ref, orderBy("description")),
    }
  );

  readonly hourTypes = new Collection(
    collection(this.firestore, `multivers/${this.id}/hourType`),
    {
      createDocument: (source, options) =>
        new HourType(source, options, this.store),
      query: (ref) => query(ref, orderBy("description"), limit(100)),
    }
  );

  readonly locations = new Collection(
    collection(this.firestore, `multivers/${this.id}/location`),
    {
      createDocument: (source, options) =>
        new Location(source, options, this.store),
      query: (ref) => query(ref, orderBy("VVLOCATIE")),
    }
  );

  readonly reports = new Collection(
    collection(this.firestore, `multivers/${this.id}/report`),
    {
      createDocument: (source, options) =>
        new Report(source, options, this.store),
      query: (ref) => query(ref, orderBy("createdAt", "desc")),
    }
  );

  employee(employeeId: string) {
    return new Employee(
      doc(this.firestore, `multivers/${this.id}/employee/${employeeId}`),
      {},
      this.store
    );
  }

  get description() {
    return this.data.description;
  }

  updateQuery = (value?: Query) => {
    if (!value) {
      this.orders.query = (ref) =>
        query(ref, orderBy("startAt", "desc"), limit(PAGE_SIZE));
    } else {
      const { filter, order, page } = value;
      this.orders.query = (ref) => {
        const constraints: QueryConstraint[] = [limit((page || 1) * PAGE_SIZE)];
        if (filter?.["startAt"]) {
          constraints.push(where("startAt", "<=", filter?.["startAt"].end));
          constraints.push(where("startAt", ">=", filter?.["startAt"].start));
        }
        if (filter?.["employee"]) {
          constraints.push(
            where(
              "employee",
              "==",
              doc(
                this.firestore,
                `multivers/${this.id}/employee/${filter?.["employee"]}`
              )
            )
          );
        }
        if (filter?.["product"]) {
          constraints.push(
            where(
              "product",
              "==",
              doc(
                this.firestore,
                `multivers/${this.id}/product/${filter?.["product"]}`
              )
            )
          );
        }
        if (filter?.["customer"]) {
          constraints.push(
            where(
              "customer",
              "==",
              doc(
                this.firestore,
                `multivers/${this.id}/customer/${filter?.["customer"]}`
              )
            )
          );
        }
        if (filter?.["location"]) {
          constraints.push(
            where(
              "location",
              "==",
              doc(
                this.firestore,
                `multivers/${this.id}/location/${filter?.["location"]}`
              )
            )
          );
        }

        if (order) {
          constraints.push(orderBy(order.field, order.direction));
        }
        return query(ref, ...constraints);
      };
    }
  };

  async addOrder(
    data: Omit<WorkOrderData, "createdAt" | "createdBy"> | NewWorkOrderData
  ) {
    const order = await addDoc(
      collection(this.firestore, `multivers/${this.id}/workorder`),
      {
        ...data,
        createdAt: Timestamp.now(),
        createdBy: this.store.auth.user?.id,
      }
    );
    return order?.id;
  }

  async downloaded(ids: string[]) {
    if (!this.ref) return;
    const batch = writeBatch(this.firestore);
    ids
      .map((id) => doc(this.firestore, `multivers/${this.id}/workorder/${id}`))
      .forEach((ref) =>
        batch.update(ref, {
          downloadedAt: Timestamp.now(),
        })
      );
    await batch.commit();
  }

  async removeWorkOrders(employee: Employee) {
    const orders = new Collection(
      collection(this.firestore, `multivers/${this.id}/workorder`),
      {
        createDocument: (source, options) =>
          new WorkOrder(source, options, this.store),
        query: (ref) => query(ref, where("employee", "==", employee.ref)),
      }
    );
    await orders.fetch();
    await Promise.all(orders.docs.map((order) => order.delete()));
  }
}

export default Administration;
